import { Candle, LiquidityZone, TrapSignal, AppSettings } from '../../../../types';
import { LiquidityCategory } from '../liquidity/liquidityEngine';
import { calcVWAP, calcRelVol, detectSwingHighs, detectSwingLows, calcSessionLevels } from '../../../../utils/calc';

// ── Zone quality helper ───────────────────────────────────────────────────────
// Returns true if a zone is structural liquidity (highest trap signal priority).
// Traps at non-structural zones are downgraded to 'Medium' severity or skipped.
function isStructural(z: LiquidityZone): boolean {
  const cat = (z as LiquidityZone & { category?: LiquidityCategory }).category;
  return cat === 'structural_liquidity' || cat === undefined; // undefined = legacy compat
}

export function detectTraps(
  candles: Candle[],
  zones: LiquidityZone[],
  settings: AppSettings
): TrapSignal[] {
  if (candles.length < 10) return [];

  const signals: TrapSignal[] = [];
  const cur = candles[candles.length - 1];
  const curPrice = cur.close;
  const vwapArr = calcVWAP(candles);
  const curVWAP = vwapArr[vwapArr.length - 1];
  const rvol = calcRelVol(candles, settings.rvolWindow);
  const levels = calcSessionLevels(candles);

  const recent20 = candles.slice(-20);
  const recent8  = candles.slice(-8);

  // ── 1. False breakout above resistance ──────────────────────────────────────
  // Only structural_liquidity zones qualify for High-severity trap signals.
  // Non-structural zones (reference, mean_anchor, psychological) → Medium at most.
  const resistances = zones.filter(z => z.type === 'resistance');
  for (const zone of resistances) {
    // Tighter reclaim: close must clearly be back below the level (not just touching)
    const brokeAbove = recent20.some(c => c.close > zone.price * 1.002);
    const nowBelow   = curPrice < zone.price * 0.9995; // must actually close below
    if (brokeAbove && nowBelow) {
      const weakVol   = rvol < 1.25;
      const structural = isStructural(zone);
      const baseSev = structural && weakVol ? 'High' : 'Medium';
      signals.push({
        type: 'Bull Trap Risk',
        level: zone.price,
        label: zone.label,
        reason: `Price breached ${zone.label} (${zone.price.toFixed(2)}) but failed to hold${structural ? ' (structural level)' : ''}. ${weakVol ? `RVOL ${rvol.toFixed(2)}x — below-average volume on break.` : 'Watch for rejection continuation.'}`,
        severity: baseSev,
        side: 'bull',
      });
    }
  }

  // ── 2. False breakdown below support (bear trap / sweep-and-reclaim) ────────
  const supports = zones.filter(z => z.type === 'support');
  for (const zone of supports) {
    // Tighten: reclaim requires close clearly above the level
    const brokeBelow = recent20.some(c => c.low < zone.price * 0.999);
    const nowAbove   = curPrice > zone.price * 1.0005; // close above, not just touching
    // Sweep-and-reclaim: wick below + close back above in the same or next bar
    const wickBelow  = recent8.some(c => c.low < zone.price && c.close > zone.price * 1.001);
    if (brokeBelow && nowAbove) {
      const structural = isStructural(zone);
      // Only promote to 'Sweep & Reclaim' at structural zones
      const isSweep = wickBelow && structural;
      signals.push({
        type: isSweep ? 'Sweep & Reclaim' : 'Bear Trap Risk',
        level: zone.price,
        label: zone.label,
        reason: isSweep
          ? `Sweep-and-reclaim at structural level ${zone.label} (${zone.price.toFixed(2)}): wick below, close above — stop-run candidate.`
          : `Price dipped below ${zone.label} (${zone.price.toFixed(2)}) but reclaimed. Monitor for re-test.`,
        severity: isSweep ? 'High' : 'Medium',
        side: 'bear',
      });
    }
  }

  // ── 3. VWAP extension — stretch / exhaustion context only ──────────────────
  // VWAP distance alone is not a trap signal — it is mean-reversion context.
  // Only elevate if additionally paired with declining volume (RVOL < 0.85 = exhaustion).
  const vwapDist = (curPrice - curVWAP) / curVWAP * 100;
  if (Math.abs(vwapDist) > 1.0 && rvol < 0.85) {
    // Exhaustion: extended AND volume fading — now actionable as exhaustion signal
    signals.push({
      type: vwapDist > 0 ? 'VWAP Stretch Exhaustion' : 'VWAP Stretch Exhaustion',
      level: curVWAP,
      label: 'Session VWAP',
      reason: `Price ${Math.abs(vwapDist).toFixed(2)}% from VWAP with fading volume (RVOL ${rvol.toFixed(2)}x) — exhaustion context, not standalone trap. Combine with structure rejection.`,
      severity: 'Medium',
      side: vwapDist > 0 ? 'bull' : 'bear',
    });
  }

  // ── 4. Weak OR breakout/breakdown — only relevant in first 2 hours ──────────
  // OR alerts become stale after the opening period; suppress later in session.
  const dt4 = new Date(cur.ts);
  const sessionHour = dt4.getHours() + dt4.getMinutes() / 60;
  const inORWindow  = sessionHour >= 9.5 && sessionHour <= 11.5; // first 2 hours only
  if (inORWindow) {
    if (curPrice > levels.orHigh * 1.002 && rvol < 0.95) {
      signals.push({
        type: 'Weak OR Breakout',
        level: levels.orHigh,
        label: 'Opening Range High',
        reason: `OR breakout on RVOL ${rvol.toFixed(2)}x — below average. Low institutional participation; watch for re-entry back inside OR.`,
        severity: 'Medium',
        side: 'bull',
      });
    }
    if (curPrice < levels.orLow * 0.998 && rvol < 0.95) {
      signals.push({
        type: 'Weak OR Breakdown',
        level: levels.orLow,
        label: 'Opening Range Low',
        reason: `OR breakdown on RVOL ${rvol.toFixed(2)}x — below average. Low conviction.`,
        severity: 'Medium',
        side: 'bear',
      });
    }
  }

  // ── 5. Prior swing sweep ─────────────────────────────────────────────────────
  if (candles.length >= settings.minSwingBars * 4) {
    const swHighs = detectSwingHighs(candles, settings.minSwingBars);
    const swLows  = detectSwingLows(candles, settings.minSwingBars);

    if (swHighs.length >= 2) {
      const ph = swHighs[swHighs.length - 2];
      const sweptAndFailed = recent8.some(c => c.high >= ph.price * 0.999) && cur.close < ph.price;
      if (sweptAndFailed) {
        signals.push({
          type: 'Prior Swing High Sweep',
          level: ph.price,
          label: 'Prior Swing High',
          reason: `Recent candles swept prior swing high (${ph.price.toFixed(2)}) but price closed below. Liquidity above that level has been collected — reversal or consolidation likely.`,
          severity: 'High',
          side: 'bull',
        });
      }
    }
    if (swLows.length >= 2) {
      const pl = swLows[swLows.length - 2];
      const sweptAndReclaimed = recent8.some(c => c.low <= pl.price * 1.001) && cur.close > pl.price;
      if (sweptAndReclaimed) {
        signals.push({
          type: 'Prior Swing Low Sweep',
          level: pl.price,
          label: 'Prior Swing Low',
          reason: `Prior swing low (${pl.price.toFixed(2)}) swept then reclaimed. Stop-loss cluster below that level has been triggered — potential upside continuation.`,
          severity: 'High',
          side: 'bear',
        });
      }
    }
  }

  // ── 6. Failed continuation (strong move → stall → reversal) ─────────────────
  const last4 = candles.slice(-4);
  if (last4.length === 4) {
    const isStalling = (Math.max(...last4.map(c => c.high)) - Math.min(...last4.map(c => c.low))) / curPrice < 0.003;
    const priorBigMove = candles.length > 6 && (() => {
      const c5 = candles[candles.length - 6];
      return Math.abs(cur.close - c5.close) / c5.close > 0.008;
    })();
    if (isStalling && priorBigMove && rvol < 0.9) {
      signals.push({
        type: 'Failed Continuation Risk',
        level: curPrice,
        label: 'Current Price',
        reason: `After a significant move, price is stalling (tight range last 4 candles) on declining volume (RVOL ${rvol.toFixed(2)}x). Classic momentum exhaustion — reversal or mean reversion is elevated risk.`,
        severity: 'Medium',
        side: 'neutral',
      });
    }
  }

  // Return empty array when no genuine trap signal found.
  // 'No Active Trap Signal' was misleading — it looked like a real trap object.
  // Callers should check signals.length === 0 for the clean case.
  return signals;
}
